import { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Loader2, X, RefreshCw, CheckCircle, AlertCircle, BookOpen, ExternalLink, Upload } from 'lucide-react';
import { adminApi, Ebook as EbookType } from '../services/adminApi';

type EbookForm = Partial<EbookType>;

const emptyForm: EbookForm = {
  title: '',
  description: '',
  imageUrl: '',
  driveLink: '',
  sortOrder: undefined,
  isActive: true,
};

// Tự sinh slug (mã định danh nội bộ) từ tiêu đề — bỏ dấu, kebab-case
function slugify(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export default function Ebook() {
  const [items, setItems] = useState<EbookType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EbookType | null>(null);
  const [form, setForm] = useState<EbookForm>(emptyForm);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState(''); // lỗi hiển thị ngay trong popup

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      setUploading(true);
      const url = await adminApi.uploadEbookImage(file);
      setField('imageUrl', url);
      showMessage('success', 'Đã tải ảnh lên');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Tải ảnh thất bại');
    } finally {
      setUploading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getEbooks();
      if (res.success && res.data) setItems(res.data);
    } catch (err) {
      showMessage('error', (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (eb: EbookType) => {
    setEditing(eb);
    setForm({ ...eb });
    setFormError('');
    setModalOpen(true);
  };

  const setField = (k: keyof EbookForm, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    // Validate — báo lỗi ngay trong popup
    if (!form.title?.trim()) {
      setFormError('Vui lòng nhập tiêu đề');
      return;
    }
    if (!form.imageUrl?.trim()) {
      setFormError('Vui lòng tải ảnh bìa lên');
      return;
    }
    if (!form.driveLink?.trim()) {
      setFormError('Vui lòng nhập Link Google Drive');
      return;
    }
    setFormError('');
    try {
      setSaving(true);
      // Chuẩn hoá "\n" (nếu dán từ JSON) thành xuống dòng thật; tự sinh slug khi tạo mới
      const title = (form.title || '').replace(/\\n/g, '\n');
      const slug = editing
        ? form.slug
        : `${slugify(title.split('\n')[0]) || 'ebook'}-${Date.now().toString(36).slice(-4)}`;
      const payload = { ...form, title, slug };
      const res = editing
        ? await adminApi.updateEbook(editing._id, payload)
        : await adminApi.createEbook(payload);
      if (res.success) {
        showMessage('success', editing ? 'Đã cập nhật ebook' : 'Đã tạo ebook');
        setModalOpen(false);
        fetchData();
      } else {
        setFormError(res.message || 'Lỗi khi lưu');
      }
    } catch (err) {
      setFormError((err as Error).message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (eb: EbookType) => {
    if (!window.confirm(`Xoá ebook "${eb.title.split('\n')[0]}"?`)) return;
    try {
      const res = await adminApi.deleteEbook(eb._id);
      if (res.success) {
        showMessage('success', 'Đã xoá ebook');
        setItems((prev) => prev.filter((x) => x._id !== eb._id));
      }
    } catch (err) {
      showMessage('error', (err as Error).message);
    }
  };

  const toggleActive = async (eb: EbookType) => {
    try {
      const res = await adminApi.updateEbook(eb._id, { isActive: !eb.isActive });
      if (res.success) setItems((prev) => prev.map((x) => (x._id === eb._id ? { ...x, isActive: !x.isActive } : x)));
    } catch (err) {
      showMessage('error', (err as Error).message);
    }
  };

  return (
    <div className="admin-container wide">
      <div className="page-head">
        <div>
          <h2 className="page-title">Quản Lý Ebook</h2>
          <p className="page-subtitle">Thêm / sửa / xoá ebook hiển thị trên trang Ebook</p>
        </div>
        <div className="row">
          <button className="btn-icon" onClick={fetchData} disabled={loading} title="Tải lại">
            <RefreshCw size={20} className={loading ? 'spin' : ''} />
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={18} /> Thêm ebook
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="center-screen">
          <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 48 }}>#</th>
                <th style={{ width: 120 }}>Ảnh</th>
                <th>Tiêu đề</th>
                <th>Link Drive</th>
                <th className="t-center">Hiện</th>
                <th className="t-center" style={{ width: 96 }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <BookOpen size={40} style={{ opacity: 0.4 }} />
                      <p>Chưa có ebook. Bấm "Thêm ebook" để tạo mới.</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map((eb) => (
                <tr key={eb._id}>
                  <td className="muted">{eb.sortOrder}</td>
                  <td>
                    {eb.imageUrl && /^(https?:\/\/|data:image\/)/.test(eb.imageUrl) ? (
                      <img
                        src={eb.imageUrl}
                        alt=""
                        style={{ width: 100, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--slate-200)', display: 'block' }}
                      />
                    ) : (
                      <div
                        style={{ width: 100, height: 68, borderRadius: 8, background: 'var(--slate-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <BookOpen size={20} className="muted" />
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 600, color: 'var(--slate-800)', whiteSpace: 'pre-line', maxWidth: 280 }}>
                    {eb.title.split('\n')[0]}
                    <span className="muted" style={{ display: 'block', fontWeight: 400, fontSize: '0.78rem' }}>
                      {eb.description}
                    </span>
                  </td>
                  <td>
                    {eb.driveLink ? (
                      <a className="link" href={eb.driveLink} target="_blank" rel="noopener noreferrer">
                        <ExternalLink size={14} style={{ marginRight: 4 }} />mở
                      </a>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="t-center">
                    <button
                      className={`switch${eb.isActive ? ' on' : ''}`}
                      onClick={() => toggleActive(eb)}
                      title={eb.isActive ? 'Đang hiện' : 'Đang ẩn'}
                    >
                      <span className="knob" />
                    </button>
                  </td>
                  <td>
                    <div className="row" style={{ justifyContent: 'center' }}>
                      <button className="btn-icon edit" onClick={() => openEdit(eb)} title="Sửa">
                        <Edit3 size={16} />
                      </button>
                      <button className="btn-icon danger" onClick={() => remove(eb)} title="Xoá">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{editing ? 'Sửa ebook' : 'Thêm ebook'}</h3>
              <button className="btn-icon" onClick={() => setModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {formError && (
                <div className="alert alert-error" style={{ marginBottom: 16 }}>
                  <AlertCircle size={18} /> {formError}
                </div>
              )}
              <div className="field">
                <label className="field-label">
                  Tiêu đề <span style={{ color: 'var(--red-600)' }}>*</span>{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>— xuống dòng dùng Enter</span>
                </label>
                <textarea className="input" rows={3} value={form.title || ''} onChange={(e) => setField('title', e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Mô tả</label>
                <textarea className="input" rows={2} value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Ảnh bìa <span style={{ color: 'var(--red-600)' }}>*</span></label>
                {form.imageUrl && /^(https?:\/\/|data:image\/)/.test(form.imageUrl) && (
                  <img
                    src={form.imageUrl}
                    alt="preview"
                    style={{ width: 120, height: 'auto', borderRadius: 8, marginBottom: 8, display: 'block', border: '1px solid var(--slate-200)' }}
                  />
                )}
                <label className="btn btn-secondary" style={{ cursor: 'pointer', marginBottom: 8 }}>
                  {uploading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />}
                  {uploading ? 'Đang tải...' : 'Tải ảnh lên'}
                  <input type="file" accept="image/*" hidden disabled={uploading} onChange={handleUpload} />
                </label>
                <input
                  className="input"
                  value={form.imageUrl || ''}
                  onChange={(e) => setField('imageUrl', e.target.value)}
                  placeholder="hoặc dán URL ảnh"
                />
              </div>
              <div className="field">
                <label className="field-label">Link Google Drive <span style={{ color: 'var(--red-600)' }}>*</span></label>
                <input className="input" value={form.driveLink || ''} onChange={(e) => setField('driveLink', e.target.value)} placeholder="https://drive.google.com/..." />
              </div>
              <div className="row-between" style={{ padding: '4px 0' }}>
                <span className="field-label" style={{ margin: 0 }}>Hiển thị trên trang Ebook</span>
                <button
                  type="button"
                  className={`switch${form.isActive ? ' on' : ''}`}
                  onClick={() => setField('isActive', !form.isActive)}
                  title={form.isActive ? 'Đang hiện' : 'Đang ẩn'}
                >
                  <span className="knob" />
                </button>
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label className="field-label">Thứ tự hiển thị</label>
                <input
                  className="input"
                  style={{ width: 120 }}
                  type="number"
                  value={form.sortOrder ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Huỷ</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <Loader2 size={18} className="spin" /> : null} Lưu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
